import { t, fmtHours } from "@/lib/i18n";
import { Clock, MapPin, Navigation, Bot, Globe, Building, Crosshair } from "lucide-react";
import { CATEGORIES, MODALITY, mapsLink, formatKm } from "@/lib/categories";

export const OfficeCard = ({ office, onAskAI, onFocus, onHover, active, index }) => {
  const cat = CATEGORIES[office.category];
  const mod = MODALITY[office.modality] || MODALITY.presencial;
  const online = office.modality !== "presencial";
  return (
    <article
      data-testid={`office-card-${office.id}`}
      onMouseEnter={() => onHover(office.id)}
      onMouseLeave={() => onHover(null)}
      style={{ animationDelay: `${Math.min(index, 8) * 35}ms`, "--cat": cat.color }}
      className={`hg-rise hg-card relative rounded-2xl border bg-ac-surface p-4 transition-[transform,box-shadow,border-color] duration-200 ${
        active ? "-translate-y-0.5 border-[var(--cat)] shadow-[0_14px_30px_-16px_rgba(0,27,62,0.45)]" : "border-ac-line"
      }`}
    >
      <div className="flex gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl" style={{ background: `${cat.color}1A`, color: cat.color }}>
          <cat.Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: cat.color }}>{cat.short}</p>
            {office.distance_km != null && (
              <span data-testid={`office-distance-${office.id}`} className="shrink-0 rounded-md bg-ac-strong px-2 py-0.5 font-mono text-[11px] font-semibold text-ac-sky">
                {formatKm(office.distance_km)}
              </span>
            )}
          </div>
          <h3 className="mt-0.5 font-display text-[15px] font-bold leading-snug text-ac-ink">{office.name}</h3>
          <p className="mt-1.5 flex gap-1.5 text-xs text-ac-muted"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="line-clamp-1">{office.address || t("osm.noAddr")}{office.municipality ? `, ${office.municipality}` : ""}</span></p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ac-muted">
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{fmtHours(office.hours)}</span>
            <span data-testid={`office-modality-${office.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${mod.tone}`}>
              {online ? <Globe className="h-3 w-3" /> : <Building className="h-3 w-3" />} {mod.label}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-[1fr_1fr_auto] gap-2">
        <button data-testid={`office-ask-ai-${office.id}`} onClick={() => onAskAI(office)} className="flex items-center justify-center gap-1.5 rounded-lg bg-ac-primary px-2 py-2 text-xs font-semibold text-white transition-colors hover:bg-ac-primary-hover">
          <Bot className="h-4 w-4" /> {t("card.ai")}
        </button>
        <a data-testid={`office-directions-${office.id}`} href={mapsLink(office)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 rounded-lg border border-ac-line px-2 py-2 text-xs font-semibold text-ac-ink transition-colors hover:bg-ac-surface2">
          <Navigation className="h-4 w-4" /> {t("popup.directions")}
        </a>
        <button data-testid={`office-show-map-${office.id}`} onClick={() => onFocus(office)} title={t("card.showMap")} aria-label={t("card.showMap")} className="grid w-9 place-items-center rounded-lg border border-ac-line text-ac-muted transition-colors hover:border-ac-strong hover:text-ac-ink">
          <Crosshair className="h-4 w-4" />
        </button>
      </div>
      {office.source === "osm" && <p className="mt-2 text-[10px] text-ac-faint">{t("card.osm")}</p>}
    </article>
  );
};
